export const MAX_CUSTOM_IR_SAMPLES = 2048;
export const CUSTOM_IR_EXACT_SAMPLES = 128;
export const CUSTOM_IR_TAIL_GROUP = 8;

const clamp = (value, low, high, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(low, Math.min(high, number)) : fallback;
};

export const normalizeImpulseSettings = (settings = {}) => ({
  normalize: settings.normalize !== false,
  autoTrim: settings.autoTrim !== false,
  trimStart: clamp(settings.trimStart, 0, 1, 0),
  trimEnd: clamp(settings.trimEnd, 0, 1, 1),
  polarityInverted: settings.polarityInverted === true,
  phaseMs: clamp(settings.phaseMs, -2, 2, 0),
});

export const preprocessImpulseChannels = (channels, sampleRate, rawSettings = {}) => {
  const settings = normalizeImpulseSettings(rawSettings);
  const usableChannels = (Array.isArray(channels) ? channels : [])
    .filter((channel) => channel && Number.isFinite(channel.length) && channel.length > 0);
  if (!usableChannels.length || !Number.isFinite(sampleRate) || sampleRate <= 0) {
    throw new Error("Impulse response tidak berisi audio yang valid");
  }
  const frameCount = Math.min(...usableChannels.map((channel) => channel.length));
  const mono = new Float32Array(frameCount);
  for (let frame = 0; frame < frameCount; frame += 1) {
    let sum = 0;
    for (const channel of usableChannels) sum += Number(channel[frame]) || 0;
    mono[frame] = sum / usableChannels.length;
  }

  let start = Math.floor(settings.trimStart * frameCount);
  let end = Math.ceil(settings.trimEnd * frameCount);
  start = Math.max(0, Math.min(frameCount - 1, start));
  end = Math.max(start + 1, Math.min(frameCount, end));
  if (settings.autoTrim) {
    let peak = 0;
    for (let index = start; index < end; index += 1) peak = Math.max(peak, Math.abs(mono[index]));
    const threshold = peak * 0.001;
    if (threshold > 0) {
      while (start < end - 1 && Math.abs(mono[start]) < threshold) start += 1;
      while (end > start + 1 && Math.abs(mono[end - 1]) < threshold) end -= 1;
    }
  }

  const phaseSamples = Math.round((settings.phaseMs / 1000) * sampleRate);
  if (phaseSamples < 0) start = Math.min(end - 1, start - phaseSamples);
  const prepend = Math.max(0, phaseSamples);
  const length = Math.min(MAX_CUSTOM_IR_SAMPLES, prepend + (end - start));
  const response = new Float32Array(length);
  const polarity = settings.polarityInverted ? -1 : 1;
  for (let index = prepend; index < length; index += 1) {
    response[index] = mono[start + index - prepend] * polarity;
  }
  if (settings.normalize) {
    let peak = 0;
    for (const sample of response) peak = Math.max(peak, Math.abs(sample));
    if (peak > 1e-12) for (let index = 0; index < response.length; index += 1) response[index] /= peak;
  }

  const tapDelays = [];
  const coefficients = [];
  const exact = Math.min(CUSTOM_IR_EXACT_SAMPLES, response.length);
  for (let index = 0; index < exact; index += 1) {
    tapDelays.push(index);
    coefficients.push(response[index]);
  }
  for (let offset = exact; offset < response.length; offset += CUSTOM_IR_TAIL_GROUP) {
    const groupEnd = Math.min(response.length, offset + CUSTOM_IR_TAIL_GROUP);
    let coefficient = 0;
    for (let index = offset; index < groupEnd; index += 1) coefficient += response[index];
    tapDelays.push(offset + Math.floor((groupEnd - offset - 1) / 2));
    coefficients.push(coefficient);
  }
  return {
    sampleRate,
    sourceLength: frameCount,
    processedLength: response.length,
    tapDelays,
    coefficients,
  };
};

export const decodeAndPreprocessImpulse = async (audioContext, arrayBuffer, settings = {}) => {
  const decoded = await audioContext.decodeAudioData(arrayBuffer.slice(0));
  const channels = Array.from({ length: decoded.numberOfChannels }, (_, index) => decoded.getChannelData(index));
  return preprocessImpulseChannels(channels, decoded.sampleRate, settings);
};
